import { Typography } from "@/components/atoms/Typography";
import {
  BedIcon,
  BuildingIcon,
  ExpandIcon,
  LandPlotIcon,
  ShowerHeadIcon,
} from "@/components/atoms/icons";
import { Accordion } from "@/components/molecules";
import { useTranslation, useUIColors } from "@/core/hooks";
import { useI18nContext } from "@/core/providers/I18nProvider";
import React, { useMemo } from "react";
import {
  ActivityIndicator,
  StyleSheet,
  TouchableOpacity,
  View,
} from "react-native";
import { useWebAmenities } from "../hooks/useWebAmenities";
import { WebAmenityIcon } from "./WebAmenityIcon";

export interface AboutPropertyWebProps {
  bedrooms: number;
  bathrooms: number;
  areaSqFt: number;
  amenities: string[];
  onViewFloorPlan?: () => void;
  floorPlanLoading?: boolean;
  isFloorPlanAvailable?: boolean;
}

export function AboutPropertyWeb({
  bedrooms,
  bathrooms,
  areaSqFt,
  amenities,
  onViewFloorPlan,
  floorPlanLoading = false,
  isFloorPlanAvailable = false
}: AboutPropertyWebProps) {
  const colors = useUIColors();
  const { t } = useTranslation();
  const { isRTL } = useI18nContext();

  const amenityItems = useWebAmenities(amenities);

  // Calculate BHK (bedrooms) and amenities count for description
  const description = useMemo(() => {
    const bhk = bedrooms || 0;
    const amenitiesCount = amenityItems.length;
    return t("propertyDetails.bhkFlat")
      .replace("{bedrooms}", bhk.toString())
      .replace("{amenitiesCount}", amenitiesCount.toString());
  }, [bedrooms, amenityItems.length, t]);

  const renderAmenities = () => {
    const rows = [];
    for (let i = 0; i < amenityItems.length; i += 3) {
      const rowAmenities = amenityItems.slice(i, i + 3);
      rows.push(
        <View key={rowAmenities[0]?.value ?? i} style={styles.amenitiesRow}>
          {rowAmenities.map(({ value, label, iconUrl }) => (
            <View key={value} style={styles.amenityItem}>
              <WebAmenityIcon
                iconUrl={iconUrl}
                label={label}
                size={24}
                defaultColor={colors.fontPrimary}
              />
              <Typography
                variant="footnote"
                color="tertiary"
                style={styles.amenityText}
              >
                {label}
              </Typography>
            </View>
          ))}
        </View>,
      );
    }
    return rows;
  };

  return (
    <Accordion
      icon={BuildingIcon}
      title={t("propertyDetails.aboutProperty")}
      subtitle={description}
      defaultExpanded={false}
    >
      <View style={styles.content}>
        <Typography
          variant="subhead"
          color="primary"
          style={[styles.sectionTitle, isRTL && styles.sectionTitleRTL]}
        >
          {t("propertyDetails.propertyInfo")}
        </Typography>

        <View style={styles.propertyInfo}>
          <View style={styles.propertyItem}>
            <View style={styles.propertyItemHeader}>
              <BedIcon size={20} color={colors.fontPrimary} />
              <Typography variant="headline" color="primary">
                {bedrooms}
              </Typography>
            </View>
            <Typography
              variant="footnote"
              color="tertiary"
              style={styles.propertyItemLabel}
            >
              {t("propertyDetails.bedrooms")}
            </Typography>
          </View>

          <View style={styles.propertyItem}>
            <View style={styles.propertyItemHeader}>
              <ShowerHeadIcon size={20} color={colors.fontPrimary} />
              <Typography variant="headline" color="primary">
                {bathrooms}
              </Typography>
            </View>
            <Typography
              variant="footnote"
              color="tertiary"
              style={styles.propertyItemLabel}
            >
              {t("propertyDetails.bathrooms")}
            </Typography>
          </View>

          <View style={styles.propertyItem}>
            <View style={styles.propertyItemHeader}>
              <ExpandIcon size={19} color={colors.fontPrimary} />
              <Typography variant="headline" color="primary">
                {areaSqFt.toLocaleString()}
              </Typography>
            </View>
            <Typography
              variant="footnote"
              color="tertiary"
              style={styles.propertyItemLabel}
            >
              {t("propertyDetails.areaInSqFt")}
            </Typography>
          </View>
        </View>
       {isFloorPlanAvailable && ( 
        <View style={styles.floorPlanButtonWrapper}>
          <TouchableOpacity
            style={[styles.floorPlanButton, isRTL && styles.floorPlanButtonRTL]}
            onPress={floorPlanLoading ? undefined : onViewFloorPlan}
            disabled={floorPlanLoading}
          >
            <View style={styles.floorPlanIconWrap}>
              <LandPlotIcon size={18} color={colors.fontPrimary} />
            </View>
            <Typography
              variant="headline"
              color="primary"
              style={styles.floorPlanText}
            >
              {t("propertyDetails.viewFloorPlan")}
            </Typography>
          </TouchableOpacity>
          {floorPlanLoading && (
            <View
              style={[
                styles.floorPlanLoadingOverlay,
                { backgroundColor: colors.backgroundLevel1 },
              ]}
            >
              <ActivityIndicator size="small" color={colors.fontPrimary} />
            </View>
          )}
        </View>
        )}
        {amenityItems.length > 0 && (
          <>
            <Typography
              variant="subhead"
              color="primary"
              style={[styles.sectionTitle, isRTL && styles.sectionTitleRTL]}
            >
              {t("propertyDetails.amenities")}
            </Typography>

            <View style={styles.amenitiesContainer}>{renderAmenities()}</View>
          </>
        )}
      </View>
    </Accordion>
  );
}

const styles = StyleSheet.create({
  content: {
    gap: 13,
  },
  sectionTitle: {
    fontWeight: "500",
  },
  sectionTitleRTL: {
    textAlign: "right",
  },
  propertyInfo: {
    flexDirection: "row",
    gap: 8,
  },
  propertyItem: {
    flex: 1,
    alignItems: "center",
    gap: 8,
  },
  propertyItemHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  propertyItemLabel: {
    textAlign: "center",
  },
  floorPlanButton: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    paddingVertical: 12,
    paddingHorizontal: 12,
    borderRadius: 4,
  },
  floorPlanButtonRTL: {
    flexDirection: "row-reverse",
  },
  floorPlanIconWrap: {
    justifyContent: "center",
    alignItems: "center",
  },
  floorPlanButtonWrapper: {
    position: "relative",
    alignItems: "center",
    justifyContent: "center",
  },
  floorPlanLoadingOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: "center",
    alignItems: "center",
    borderRadius: 4,
    opacity: 0.85,
  },
  floorPlanText: {
    fontWeight: "600",
    textDecorationLine: "underline",
    marginBottom: 0,
    lineHeight: 18,
  },
  amenitiesContainer: {
    gap: 0,
  },
  amenitiesRow: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 8,
  },
  amenityItem: {
    flex: 1,
    alignItems: "center",
    gap: 8,
    paddingHorizontal: 8,
  },
  amenityText: {
    textAlign: "center",
    height: 39,
    justifyContent: "center",
  },
});

export default AboutPropertyWeb;
